import { c as _c } from "react-compiler-runtime";
import { createContext, forwardRef, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, useFieldIds } from "@/components/form";
import { Text } from "@/components/primitives";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/core/utils";
type CheckboxFieldContextValue = {
  inputId: string;
  labelId: string;
  hintId: string;
  errorId: string;
};
const CheckboxFieldContext = createContext<CheckboxFieldContextValue | null>(null);
function useCheckboxFieldContext() {
  const ctx = useContext(CheckboxFieldContext);
  if (!ctx) {
    throw new globalThis.Error("CheckboxField components must be used within CheckboxField.Root");
  }
  return ctx;
}
type CheckboxLabelProps = {
  text: React.ReactNode;
  invalid?: boolean;
};
function CheckboxLabel(t0) {
  const $ = _c(6);
  const {
    text,
    invalid
  } = t0;
  if (typeof text !== "string") {
    const t1 = invalid ? "destructive" : "foreground";
    let t2;
    if ($[0] !== t1 || $[1] !== text) {
      t2 = <Text component="span" tone={t1} variant="label-md">{text}</Text>;
      $[0] = t1;
      $[1] = text;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = invalid ? "destructive" : "foreground";
  let t2;
  if ($[3] !== t1 || $[4] !== text) {
    t2 = <Text component="span" text={text} tone={t1} variant="label-md" />;
    $[3] = t1;
    $[4] = text;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
type CheckboxHintProps = {
  text: string;
};
function CheckboxHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useCheckboxFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type CheckboxErrorMessageProps = {
  text: string;
};
function CheckboxErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    inputId,
    errorId
  } = useCheckboxFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== inputId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={inputId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = inputId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
  className?: string;
};
function Root(t0) {
  const $ = _c(13);
  const {
    children,
    name,
    className
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.errorId || $[1] !== fieldIds.hintId || $[2] !== fieldIds.inputId || $[3] !== fieldIds.labelId) {
    t1 = {
      inputId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId
    };
    $[0] = fieldIds.errorId;
    $[1] = fieldIds.hintId;
    $[2] = fieldIds.inputId;
    $[3] = fieldIds.labelId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const value = t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("grid gap-0.5 text-left", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[7] = children;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3 || $[11] !== value) {
    t4 = <CheckboxFieldContext.Provider value={value}>{t3}</CheckboxFieldContext.Provider>;
    $[10] = t3;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
type CheckboxRowProps = {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
};
function CheckboxRow(t0) {
  const $ = _c(8);
  const {
    children,
    className,
    disabled
  } = t0;
  const {
    inputId,
    labelId
  } = useCheckboxFieldContext();
  const t1 = disabled ? "cursor-not-allowed" : "cursor-pointer";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("flex items-start gap-2", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== inputId || $[5] !== labelId || $[6] !== t2) {
    t3 = <label className={t2} htmlFor={inputId} id={labelId}>{children}</label>;
    $[3] = children;
    $[4] = inputId;
    $[5] = labelId;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
type InputProps = React.ComponentPropsWithoutRef<typeof Checkbox>;
const Input = forwardRef<React.ElementRef<typeof Checkbox>, InputProps>(function Input(props, ref) {
  const $ = _c(19);
  const {
    errorId,
    hintId,
    inputId,
    labelId
  } = useCheckboxFieldContext();
  let className;
  let onBlur;
  let onCheckedChange;
  let rest;
  if ($[0] !== props) {
    const {
      id: _id,
      className: t0,
      onBlur: t1,
      onCheckedChange: t2,
      ...t3
    } = props;
    className = t0;
    onBlur = t1;
    onCheckedChange = t2;
    rest = t3;
    $[0] = props;
    $[1] = className;
    $[2] = onBlur;
    $[3] = onCheckedChange;
    $[4] = rest;
  } else {
    className = $[1];
    onBlur = $[2];
    onCheckedChange = $[3];
    rest = $[4];
  }
  const invalid = !!props["aria-invalid"];
  const t0 = `${hintId} ${errorId}`;
  const t1 = props["aria-errormessage"];
  const t2 = invalid || undefined;
  const t3 = invalid && "border-red-600 data-[state=checked]:bg-red-600";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== inputId || $[9] !== labelId || $[10] !== onBlur || $[11] !== onCheckedChange || $[12] !== ref || $[13] !== rest || $[14] !== t0 || $[15] !== t1 || $[16] !== t2 || $[17] !== t4) {
    t5 = <Checkbox {...rest} aria-describedby={t0} aria-errormessage={t1} aria-invalid={t2} aria-labelledby={labelId} className={t4} id={inputId} ref={ref} onBlur={onBlur} onCheckedChange={onCheckedChange} />;
    $[8] = inputId;
    $[9] = labelId;
    $[10] = onBlur;
    $[11] = onCheckedChange;
    $[12] = ref;
    $[13] = rest;
    $[14] = t0;
    $[15] = t1;
    $[16] = t2;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
});
Input.displayName = "CheckboxField.Input";
type NativeProps = {
  name: string;
  label: React.ReactNode;
  hint?: string;
  inputProps: InputProps;
  className?: string;
};
function Native(t0) {
  const $ = _c(20);
  const {
    name,
    label,
    hint,
    inputProps,
    className
  } = t0;
  const disabled = !!inputProps.disabled;
  let t1;
  if ($[0] !== inputProps) {
    t1 = <Input {...inputProps} />;
    $[0] = inputProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = disabled && "opacity-50";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid gap-0.5", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <CheckboxLabel text={label} />;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== disabled || $[10] !== t1 || $[11] !== t5) {
    t6 = <CheckboxRow disabled={disabled}>{t1}{t5}</CheckboxRow>;
    $[9] = disabled;
    $[10] = t1;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== hint) {
    t7 = hint && <CheckboxHint text={hint} />;
    $[13] = hint;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== className || $[16] !== name || $[17] !== t6 || $[18] !== t7) {
    t8 = <Root className={className} name={name}>{t6}{t7}</Root>;
    $[15] = className;
    $[16] = name;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
type RHF<T extends FieldValues> = {
  name: Path<T>;
  label: React.ReactNode;
  hint?: string;
  className?: string;
  inputProps?: Omit<InputProps, "id" | "name" | "checked" | "defaultChecked" | "aria-invalid" | "aria-errormessage">;
};
function RHF(t0) {
  const $ = _c(46);
  const {
    name,
    label,
    hint,
    className,
    inputProps
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = {
      name
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    field,
    fieldState
  } = useController(t1);
  const errorText = fieldState.error?.message;
  const invalid = !!errorText;
  const value = typeof field.value === "boolean" ? field.value : false;
  let t2;
  if ($[2] !== inputProps) {
    t2 = inputProps ?? {};
    $[2] = inputProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let onBlurProp;
  let onCheckedChangeProp;
  let restInputProps;
  if ($[4] !== t2) {
    ({
      onCheckedChange: onCheckedChangeProp,
      onBlur: onBlurProp,
      ...restInputProps
    } = t2);
    $[4] = t2;
    $[5] = onBlurProp;
    $[6] = onCheckedChangeProp;
    $[7] = restInputProps;
  } else {
    onBlurProp = $[5];
    onCheckedChangeProp = $[6];
    restInputProps = $[7];
  }
  const disabled = !!restInputProps.disabled;
  let t3;
  if ($[8] !== field || $[9] !== onCheckedChangeProp) {
    t3 = checked => {
      onCheckedChangeProp?.(checked);
      field.onChange(checked === true);
    };
    $[8] = field;
    $[9] = onCheckedChangeProp;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleCheckedChange = t3;
  let t4;
  if ($[11] !== field || $[12] !== onBlurProp) {
    t4 = event => {
      onBlurProp?.(event);
      field.onBlur();
    };
    $[11] = field;
    $[12] = onBlurProp;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleBlur = t4;
  const t5 = fieldState.error?.message;
  const t6 = invalid || undefined;
  let t7;
  if ($[14] !== field.name || $[15] !== handleBlur || $[16] !== handleCheckedChange || $[17] !== restInputProps || $[18] !== t5 || $[19] !== t6 || $[20] !== value) {
    t7 = <Input aria-errormessage={t5} aria-invalid={t6} {...restInputProps} checked={value} name={field.name} onBlur={handleBlur} onCheckedChange={handleCheckedChange} />;
    $[14] = field.name;
    $[15] = handleBlur;
    $[16] = handleCheckedChange;
    $[17] = restInputProps;
    $[18] = t5;
    $[19] = t6;
    $[20] = value;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = disabled && "opacity-50";
  let t9;
  if ($[22] !== t8) {
    t9 = cn("grid gap-1", t8);
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== invalid || $[25] !== label) {
    t10 = <CheckboxLabel invalid={invalid} text={label} />;
    $[24] = invalid;
    $[25] = label;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== disabled || $[31] !== t11 || $[32] !== t7) {
    t12 = <CheckboxRow disabled={disabled}>{t7}{t11}</CheckboxRow>;
    $[30] = disabled;
    $[31] = t11;
    $[32] = t7;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== hint) {
    t13 = hint && <CheckboxHint text={hint} />;
    $[34] = hint;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== errorText) {
    t14 = errorText && <CheckboxErrorMessage text={errorText} />;
    $[36] = errorText;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = <div className="h-4">{t14}</div>;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== className || $[41] !== name || $[42] !== t12 || $[43] !== t13 || $[44] !== t15) {
    t16 = <Root className={className} name={name}>{t12}{t13}{t15}</Root>;
    $[40] = className;
    $[41] = name;
    $[42] = t12;
    $[43] = t13;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}
export const CheckboxField = {
  RHF,
  Native
};